import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getEdgeById } from "../api/edgeApi";
import {
  getDashboardEdgeSystem,
  getDashboardEdgeDevices,
  getDashboardDataLatest,
} from "../api/dashboardApi";

export default function EdgeDetail() {
  const { edgeDbId } = useParams();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session?.accessToken);

  const [edge, setEdge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("sensor_latest");
  const [sensorRows, setSensorRows] = useState([]);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLoading, setErrorLoading] = useState(false);

  useEffect(() => {
    if (!accessToken || !edgeDbId) return;

    async function fetchEdge() {
      try {
        setLoading(true);
        const data = await getEdgeById(accessToken, edgeDbId);
        setEdge(data);
      } catch (err) {
        setError(err.response?.data?.detail || "Edge bulunamadı");
      } finally {
        setLoading(false);
      }
    }

    fetchEdge();
  }, [accessToken, edgeDbId]);

  const fetchSensorLatest = useCallback(async () => {
    if (!accessToken || !edge?.edgeId) return;
    setSensorLoading(true);
    try {
      const devices = await getDashboardEdgeDevices(accessToken, edge.edgeId);
      const deviceIds = (devices || []).map((d) => d.id);
      if (!deviceIds.length) { setSensorRows([]); return; }
      const latest = await getDashboardDataLatest(accessToken, edge.edgeId, deviceIds);
      setSensorRows(Array.isArray(latest) ? latest : []);
    } catch (_) {
      setSensorRows([]);
    } finally {
      setSensorLoading(false);
    }
  }, [accessToken, edge?.edgeId]);

  const fetchErrorLogs = useCallback(async () => {
    if (!accessToken || !edge?.edgeId) return;
    setErrorLoading(true);
    try {
      const record = await getDashboardEdgeSystem(accessToken, edge.edgeId);
      let errors = {};
      try {
        if (record?.device_errors && record.device_errors !== "{}") {
          errors = typeof record.device_errors === "string"
            ? JSON.parse(record.device_errors)
            : record.device_errors;
        }
      } catch (_) {}
      const rows = Object.entries(errors).map(([device, msg]) => ({
        ts: record.ts ?? record.timestamp,
        device,
        msg,
      }));
      setErrorLogs(rows);
    } catch (_) {
      setErrorLogs([]);
    } finally {
      setErrorLoading(false);
    }
  }, [accessToken, edge?.edgeId]);

  useEffect(() => {
    if (activeTab === "sensor_latest") fetchSensorLatest();
    else if (activeTab === "error_logs") fetchErrorLogs();
  }, [activeTab, fetchSensorLatest, fetchErrorLogs]);

  if (loading) {
    return <div style={styles.container}>Yükleniyor...</div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p>Hata: {error}</p>
        <button onClick={() => navigate("/edges")}>Geri Dön</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/edges")}>
          ← Geri
        </button>
        <div>
          <h1 style={styles.title}>{edge.edgeId}</h1>
          <p style={styles.subtitle}>
            {edge.asset.type} - {edge.asset.location}
          </p>
        </div>
        <span style={styles.statusBadge}>Aktif</span>
      </div>

      {/* Edge Info Cards */}
      <div style={styles.infoGrid}>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>Cihaz Bilgileri</h3>
          <p>
            <strong>Üretici:</strong> {edge.device.manufacturer}
          </p>
          <p>
            <strong>Model:</strong> {edge.device.model}
          </p>
          <p>
            <strong>Firmware:</strong> {edge.device.firmware}
          </p>
        </div>
        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>Varlık Bilgileri</h3>
          <p>
            <strong>Asset ID:</strong> {edge.asset.assetId}
          </p>
          <p>
            <strong>Tip:</strong> {edge.asset.type}
          </p>
          <p>
            <strong>Lokasyon:</strong> {edge.asset.location}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={activeTab === "sensor_latest" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("sensor_latest")}
        >
          Son Alınan Sensör Verileri
        </button>
        <button
          style={activeTab === "error_logs" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("error_logs")}
        >
          Hata Logları
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === "sensor_latest" && (
          <SensorLatestTab rows={sensorRows} loading={sensorLoading} onRefresh={fetchSensorLatest} />
        )}
        {activeTab === "error_logs" && (
          <ErrorLogsTab logs={errorLogs} loading={errorLoading} onRefresh={fetchErrorLogs} />
        )}
      </div>
    </div>
  );
}

// Sensör Verileri Tab (placeholder - InfluxDB entegrasyonu sonra eklenecek)
// eslint-disable-next-line no-unused-vars
function MetricsTab({ edgeId }) {
  return (
    <div style={styles.placeholder}>
      <h3>Sensör Verileri - {edgeId}</h3>
      <p>Grafikler burada görüntülenecek:</p>
      <ul>
        <li>Sıcaklık (Temperature)</li>
        <li>Titreşim (Vibration)</li>
        <li>Basınç (Pressure)</li>
        <li>Voltaj (Voltage)</li>
        <li>Akım (Current)</li>
      </ul>
      <p style={{ color: "#8b949e", marginTop: "16px" }}>
        InfluxDB entegrasyonu yapıldığında gerçek veriler burada gösterilecek.
      </p>
    </div>
  );
}

// Alarmlar Tab (placeholder)
// eslint-disable-next-line no-unused-vars
function AlarmsTab({ edgeId }) {
  return (
    <div style={styles.placeholder}>
      <h3>Alarm Geçmişi - {edgeId}</h3>
      <p>Tetiklenen alarmlar burada listelenecek.</p>
    </div>
  );
}

// Sistem Durumu Tab
// eslint-disable-next-line no-unused-vars
function SystemTab({ data, loading, onRefresh }) {
  if (loading) {
    return <div style={{ padding: "32px", color: "#8b949e", textAlign: "center" }}>Yükleniyor...</div>;
  }
  if (!data) {
    return (
      <div style={{ padding: "32px", textAlign: "center" }}>
        <p style={{ color: "#8b949e" }}>Sistem verisi alınamadı.</p>
        <button style={sysStyles.refreshBtn} onClick={onRefresh}>Yenile</button>
      </div>
    );
  }

  const cpu = data.cpu_percent ?? null;
  const mem = data.mem_percent ?? null;
  const disk = data.disk_percent ?? null;
  const deviceCount = data.device_count ?? null;
  const deviceConnected = data.device_connected ?? null;
  const deviceErrorCount = data.device_error_count ?? 0;
  const ts = data.ts ?? data.timestamp ?? null;

  let deviceErrors = {};
  try {
    if (data.device_errors && data.device_errors !== "{}") {
      deviceErrors = typeof data.device_errors === "string"
        ? JSON.parse(data.device_errors)
        : data.device_errors;
    }
  } catch (_) {}

  const pctColor = (v) => {
    if (v == null) return "#8b949e";
    if (v >= 90) return "#f85149";
    if (v >= 70) return "#f0883e";
    return "#3fb950";
  };

  return (
    <div style={{ color: "#e6edf3" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#58a6ff" }}>Sistem Kaynakları</h3>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {ts && (
            <span style={{ fontSize: "12px", color: "#6e7681" }}>
              Son güncelleme: {new Date(ts).toLocaleString("tr-TR")}
            </span>
          )}
          <button style={sysStyles.refreshBtn} onClick={onRefresh}>↻ Yenile</button>
        </div>
      </div>

      {/* CPU / RAM / Disk */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        {[
          { label: "CPU", value: cpu },
          { label: "RAM", value: mem },
          { label: "Disk", value: disk },
        ].map(({ label, value }) => (
          <div key={label} style={sysStyles.metricCard}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "14px", color: "#8b949e" }}>{label}</span>
              <span style={{ fontSize: "18px", fontWeight: 700, color: pctColor(value) }}>
                {value != null ? `${Number(value).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div style={sysStyles.progressBg}>
              <div
                style={{
                  ...sysStyles.progressBar,
                  width: value != null ? `${Math.min(100, value)}%` : "0%",
                  backgroundColor: pctColor(value),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Cihaz Durumu */}
      {deviceCount != null && (
        <div style={sysStyles.deviceSection}>
          <h3 style={{ margin: "0 0 16px", fontSize: "16px", color: "#58a6ff" }}>Cihaz Durumu</h3>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "16px" }}>
            <div style={sysStyles.deviceStat}>
              <span style={{ color: "#8b949e", fontSize: "13px" }}>Toplam</span>
              <span style={{ fontSize: "22px", fontWeight: 700, color: "#e6edf3" }}>{deviceCount}</span>
            </div>
            <div style={sysStyles.deviceStat}>
              <span style={{ color: "#8b949e", fontSize: "13px" }}>Bağlı</span>
              <span style={{ fontSize: "22px", fontWeight: 700, color: "#3fb950" }}>
                {deviceConnected ?? "—"}
              </span>
            </div>
            <div style={sysStyles.deviceStat}>
              <span style={{ color: "#8b949e", fontSize: "13px" }}>Hatalı</span>
              <span style={{ fontSize: "22px", fontWeight: 700, color: deviceErrorCount > 0 ? "#f85149" : "#3fb950" }}>
                {deviceErrorCount}
              </span>
            </div>
          </div>

          {/* Hata detayları */}
          {Object.keys(deviceErrors).length > 0 && (
            <div>
              <p style={{ fontSize: "13px", color: "#f85149", marginBottom: "8px" }}>Cihaz Hataları:</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {Object.entries(deviceErrors).map(([device, msg]) => (
                  <div key={device} style={sysStyles.errorBadge}>
                    <span style={{ fontWeight: 600 }}>{device}</span>
                    <span style={{ color: "#f85149" }}>{msg}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const sysStyles = {
  refreshBtn: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "4px 12px",
    cursor: "pointer",
    fontSize: "12px",
  },
  metricCard: {
    backgroundColor: "#0d1117",
    border: "1px solid #30363d",
    borderRadius: "8px",
    padding: "16px",
  },
  progressBg: {
    width: "100%",
    height: "8px",
    backgroundColor: "#21262d",
    borderRadius: "4px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  deviceSection: {
    backgroundColor: "#0d1117",
    border: "1px solid #30363d",
    borderRadius: "8px",
    padding: "16px",
  },
  deviceStat: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "center",
    minWidth: "80px",
  },
  errorBadge: {
    backgroundColor: "rgba(248,81,73,0.1)",
    border: "1px solid rgba(248,81,73,0.3)",
    borderRadius: "6px",
    padding: "8px 12px",
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    fontSize: "13px",
    color: "#e6edf3",
  },
};

// Son Alınan Sensör Verileri Tab
function SensorLatestTab({ rows, loading, onRefresh }) {
  if (loading) return <div style={{ padding: "32px", color: "#8b949e", textAlign: "center" }}>Yükleniyor...</div>;

  return (
    <div style={{ color: "#e6edf3" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#58a6ff" }}>Son Alınan Sensör Verileri</h3>
        <button style={sysStyles.refreshBtn} onClick={onRefresh}>↻ Yenile</button>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center", color: "#8b949e" }}>
          Sensör verisi bulunamadı.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ backgroundColor: "#0d1117" }}>
                {["Cihaz", "Tag", "Değer", "Birim", "Zaman"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#8b949e", borderBottom: "1px solid #30363d", fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #21262d" }}>
                  <td style={{ padding: "9px 14px", color: "#e6edf3" }}>{r.device_name ?? r.device_id ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "#58a6ff" }}>{r.tag_label ?? r.tag_name ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "#e6edf3", fontWeight: 600 }}>
                    {r.value != null ? Number(r.value).toFixed(3) : "—"}
                  </td>
                  <td style={{ padding: "9px 14px", color: "#8b949e" }}>{r.unit ?? "—"}</td>
                  <td style={{ padding: "9px 14px", color: "#6e7681", fontSize: "12px" }}>
                    {r.time ? new Date(r.time).toLocaleString("tr-TR") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Hata Logları Tab
function ErrorLogsTab({ logs, loading, onRefresh }) {
  if (loading) return <div style={{ padding: "32px", color: "#8b949e", textAlign: "center" }}>Yükleniyor...</div>;

  return (
    <div style={{ color: "#e6edf3" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#f85149" }}>
          Hata Logları
          {logs.length > 0 && (
            <span style={{
              marginLeft: "10px",
              backgroundColor: "rgba(248,81,73,0.2)",
              border: "1px solid rgba(248,81,73,0.4)",
              borderRadius: "10px",
              padding: "2px 8px",
              fontSize: "12px",
              color: "#f85149",
            }}>
              {logs.length}
            </span>
          )}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", color: "#6e7681" }}>Son 24 saat</span>
          <button style={sysStyles.refreshBtn} onClick={onRefresh}>↻ Yenile</button>
        </div>
      </div>
      {logs.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>✓</div>
          <div style={{ color: "#3fb950", fontSize: "15px", fontWeight: 600 }}>Hata yok</div>
          <div style={{ color: "#8b949e", fontSize: "13px", marginTop: "4px" }}>Son 24 saatte cihaz hatası kaydedilmedi.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {logs.map((log, i) => (
            <div key={i} style={{
              display: "flex",
              gap: "16px",
              alignItems: "flex-start",
              backgroundColor: "rgba(248,81,73,0.07)",
              border: "1px solid rgba(248,81,73,0.25)",
              borderRadius: "8px",
              padding: "12px 16px",
            }}>
              <span style={{
                flexShrink: 0,
                backgroundColor: "rgba(248,81,73,0.2)",
                border: "1px solid rgba(248,81,73,0.4)",
                borderRadius: "6px",
                padding: "3px 10px",
                fontSize: "12px",
                color: "#f85149",
                fontWeight: 700,
              }}>
                {log.device}
              </span>
              <span style={{ flex: 1, fontSize: "13px", color: "#e6edf3", lineHeight: 1.5 }}>{log.msg}</span>
              <span style={{ flexShrink: 0, fontSize: "11px", color: "#6e7681" }}>
                {log.ts ? new Date(log.ts).toLocaleString("tr-TR") : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Alarm Kuralları Tab (placeholder)
// eslint-disable-next-line no-unused-vars
function RulesTab({ edgeDbId }) {
  return (
    <div style={styles.placeholder}>
      <h3>Alarm Kuralları</h3>
      <p>Threshold tanımlamaları burada yapılacak.</p>
      <button style={styles.addBtn}>+ Yeni Kural Ekle</button>
    </div>
  );
}

const styles = {
  container: {
    padding: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
    color: "#e6edf3",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  backBtn: {
    padding: "8px 16px",
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "8px",
    cursor: "pointer",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: 0,
    color: "#e6edf3",
  },
  subtitle: {
    color: "#8b949e",
    margin: 0,
  },
  statusBadge: {
    marginLeft: "auto",
    backgroundColor: "#238636",
    color: "#fff",
    padding: "6px 16px",
    borderRadius: "16px",
    fontSize: "14px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  infoCard: {
    backgroundColor: "#161b22",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #30363d",
    color: "#e6edf3",
  },
  infoTitle: {
    fontSize: "14px",
    color: "#58a6ff",
    textTransform: "uppercase",
    marginBottom: "12px",
  },
  tabs: {
    display: "flex",
    gap: "8px",
    borderBottom: "2px solid #30363d",
    marginBottom: "24px",
  },
  tab: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    fontSize: "14px",
    color: "#8b949e",
    marginBottom: "-2px",
  },
  tabActive: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid #58a6ff",
    cursor: "pointer",
    fontSize: "14px",
    color: "#58a6ff",
    fontWeight: "bold",
    marginBottom: "-2px",
  },
  tabContent: {
    backgroundColor: "#161b22",
    padding: "24px",
    borderRadius: "12px",
    border: "1px solid #30363d",
  },
  placeholder: {
    textAlign: "center",
    padding: "32px",
    color: "#e6edf3",
  },
  addBtn: {
    marginTop: "16px",
    padding: "12px 24px",
    backgroundColor: "#238636",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
  },
};
